import * as React from 'react';

import { BillType, enumBillType } from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import { $columnSelectedTypes } from '~/modules/finance/_state/financeBills.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface TypeOption {
  id: BillType;
  label: string;
}

interface BillTypeColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ BILL TYPE COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const BillTypeColumnFilter: React.FC<BillTypeColumnFilterProps> = ({
  onClose,
}) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedTypesSet, setSelectedTypesSet] = useAtom($columnSelectedTypes);

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const selectedTypes = React.useMemo(
    () => Array.from(selectedTypesSet),
    [selectedTypesSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedTypes),
    [selectedTypes],
  );

  const typeOptions: TypeOption[] = React.useMemo(
    () =>
      Object.values(enumBillType).map((type) => ({
        id: type,
        label: t.enums.billType[type] ?? type,
      })),
    [t.enums.billType],
  );

  const filteredTypeOptions = React.useMemo(
    () =>
      typeOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [typeOptions, searchQuery],
  );

  const selectedTypeOptions = React.useMemo(
    () => typeOptions.filter((option) => selectedTypes.includes(option.id)),
    [typeOptions, selectedTypes],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedTypesSet(new Set(Array.from(keys) as BillType[]));
    },
    [setSelectedTypesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedTypesSet(new Set());
  }, [setSelectedTypesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: TypeOption, isSelected: boolean) => (
      <FilterLabel label={item.label} isSelected={isSelected} />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@BillTypeColumnFilter min-w-[250px]"
      entries={filteredTypeOptions}
      selectedEntries={selectedTypeOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(type) => type.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.bills.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
